import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TableFooter
} from '@mui/material';
import { format } from 'date-fns';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { JobWorkReceivedItem } from '../../api/jobWorks';
import ViewReceiptDialog from './ViewReceiptDialog';

interface JobWorkReceiptsProps {
  receipts: JobWorkReceivedItem[];
  onEditReceipt: (receipt: JobWorkReceivedItem) => void;
  onPrintReceipt: (receipt: JobWorkReceivedItem) => void;
  onDeleteReceipt: (receiptId: number) => Promise<void>;
  isCompleted?: boolean;
  processMaterial: boolean;
  jobWork: any;
}

const JobWorkReceipts: React.FC<JobWorkReceiptsProps> = ({
  receipts,
  onEditReceipt,
  onPrintReceipt,
  onDeleteReceipt,
  isCompleted = false,
  processMaterial,
  jobWork
}) => {
  const isInvoiced = !!(jobWork?.invoiceNumber);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [selectedReceiptId, setSelectedReceiptId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState<JobWorkReceivedItem | null>(null);

  // Log receipts for debugging
  console.log('JobWorkReceipts - Receipts received:', receipts);

  const handleDeleteClick = (receiptId: number) => {
    // Ensure receiptId is a valid number
    if (!receiptId || isNaN(receiptId) || receiptId <= 0) {
      console.error("Invalid receipt ID:", receiptId);
      return;
    }
    setSelectedReceiptId(receiptId);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedReceiptId && !isNaN(selectedReceiptId) && selectedReceiptId > 0) {
      setIsDeleting(true);
      try {
        await onDeleteReceipt(selectedReceiptId);
      } finally {
        setIsDeleting(false);
        setConfirmDeleteOpen(false);
        setSelectedReceiptId(null);
      }
    } else {
      console.error("Invalid receipt ID for deletion:", selectedReceiptId);
      setConfirmDeleteOpen(false);
      setSelectedReceiptId(null);
    }
  };

  const handleCloseConfirm = () => {
    setConfirmDeleteOpen(false);
    setSelectedReceiptId(null);
  };

  const handleViewClick = (receipt: JobWorkReceivedItem) => {
    setSelectedReceipt(receipt);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedReceipt(null);
  };

  const calculateTotalQuantity = (receipts: JobWorkReceivedItem[]): number => {
    return receipts.reduce((total, receipt) => {
      if (processMaterial) {
        return total + (receipt.materialQuantity || 0);
      } else {
        return total + (receipt.pieceCount || 0) + (receipt.defectQuantity || 0) + (receipt.shortageQuantity || 0);
      }
    }, 0);
  };

  const calculateTotalPieceCount = (receipts: JobWorkReceivedItem[]): number => {
    return receipts.reduce((total, receipt) => total + (receipt.pieceCount || 0), 0);
  };

  const calculateTotalDefects = (receipts: JobWorkReceivedItem[]): number => {
    return receipts.reduce((total, receipt) => total + (receipt.defectQuantity || 0), 0);
  };

  const calculateTotalShortages = (receipts: JobWorkReceivedItem[]): number => {
    return receipts.reduce((total, receipt) => total + (receipt.shortageQuantity || 0), 0);
  };

  return (
    <>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Received Items
        </Typography>
        
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Design</TableCell>
                <TableCell>Part</TableCell>
                <TableCell>Size</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell align="right">Defect Qty</TableCell>
                <TableCell align="right">Shortage Qty</TableCell>
                <TableCell align="right">Rate</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {receipts.length > 0 ? (
                receipts.map((receipt) => {
                  console.log('Rendering receipt with ID:', receipt.id, typeof receipt.id);
                  return (
                  <TableRow key={receipt.id}>
                    <TableCell>
                      {format(new Date(receipt.createdAt), 'dd/MM/yyyy')}
                    </TableCell>
                    <TableCell>{receipt.designNo || 'N/A'}</TableCell>
                    <TableCell>{receipt.garmentPart || 'N/A'}</TableCell>
                    <TableCell>{receipt.size || 'N/A'}</TableCell>
                    <TableCell align="right">
                      {processMaterial ? (receipt.materialQuantity || 0) : (receipt.pieceCount || 0)}
                    </TableCell>
                    <TableCell align="right">
                      {receipt.defectQuantity || 0}
                    </TableCell>
                    <TableCell align="right">
                      {receipt.shortageQuantity || 0}
                    </TableCell>
                    <TableCell align="right">
                      {receipt.processingRate ? `₹${receipt.processingRate.toFixed(2)}` : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Box display="flex">
                        <Tooltip title="View Receipt">
                          <IconButton
                            size="small"
                            onClick={() => handleViewClick(receipt)}
                            color="primary"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {!isInvoiced && (
                          <Tooltip title="Edit Receipt">
                            <IconButton
                              size="small"
                              onClick={() => onEditReceipt(receipt)}
                              color="info"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!isCompleted && (
                          <Tooltip title="Delete Receipt">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(receipt.id)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Print Receipt">
                          <IconButton
                            size="small"
                            onClick={() => onPrintReceipt(receipt)}
                            color="secondary"
                          >
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No received items found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            {receipts.length > 0 && (
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={4} align="right" sx={{ fontWeight: 'bold' }}>
                    Total:
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {calculateTotalQuantity(receipts)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {calculateTotalDefects(receipts)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {calculateTotalShortages(receipts)}
                  </TableCell>
                  <TableCell align="right" /> {/* Rate column */}
                  <TableCell /> {/* Actions column */}
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
        
        {receipts.length > 0 && (
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Typography variant="subtitle1">
              Total Received Items: {receipts.length} | 
              Total Quantity: {calculateTotalQuantity(receipts)} {processMaterial ? '' : '(Including defects and shortages)'}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={handleCloseConfirm}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Delete Received Item
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this received item? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            disabled={isDeleting}
            variant="contained"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Receipt Dialog */}
      {selectedReceipt && (
        <ViewReceiptDialog
          open={viewDialogOpen}
          onClose={handleCloseViewDialog}
          receipt={selectedReceipt}
          processMaterial={processMaterial}
          invoiceDetails={{
            invoiceNumber: jobWork.invoiceNumber,
            invoiceDate: jobWork.invoiceDate,
            invoiceAmount: jobWork.invoiceAmount,
            taxAmount: jobWork.taxAmount,
            invoiceImageUrl: jobWork.invoiceImageUrl
          }}
        />
      )}
    </>
  );
};

export default JobWorkReceipts; 